import { createBindingContext, createPath, IEntity } from "@odata/BindingContext";
import {
    AddressEntity,
    BillingAddressEntity,
    EntitySetName,
    IBillingPricingEntity,
    ITenantEntity,
    TenantEntity
} from "@odata/GeneratedEntityTypes";
import { BillingModuleTypeCode, CurrencyCode, ProductCode } from "@odata/GeneratedEnums";
import { parseError } from "@odata/ODataParser";
import { WithOData, withOData } from "@odata/withOData";
import { getDefinition } from "@pages/admin/subscriptions/subpages/Invoicing.def";
import { isEduLicenceWaitingForConfirmation } from "@pages/admin/subscriptions/Subscriptions.utils";
import { IDefinition } from "@pages/PageUtils";
import i18next from "i18next";
import React from "react";
import { Trans, WithTranslation, withTranslation } from "react-i18next";
import { RouteComponentProps, withRouter } from "react-router-dom";

import { AppContext, IAppContext } from "../../contexts/appContext/AppContext.types";
import { BasicInputSizes, FieldType, IconSize, LabelStatus, Status } from "../../enums";
import { EvalaTermsAndConditionsEduLink, EvalaTermsAndConditionsLink } from "../../login/Login.utils";
import { IValidationError } from "../../model/Validator.types";
import { ROUTE_SUBSCRIPTIONS_PLAN } from "../../routes";
import billingPayCardSvg from "../../svg/menu/billingPayCard.svg"; // origName: menu+icons/svg/menu icons all_PREDPLATNE_platební údaje.svg
import billingPayWireSvg from "../../svg/menu/billingPayWire.svg"; // origName: menu+icons/svg/menu icons all_PREDPLATNE_platbe prevodem.svg
import TestIds from "../../testIds";
import Currency from "../../types/Currency";
import memoizeOneWithParamsCheck from "../../utils/memoizeOne";
import { getAlertFromError } from "../../views/formView/Form.utils";
import { FormViewForExtend } from "../../views/formView/FormView";
import PlugAndPlayForm from "../../views/formView/PlugAndPlayForm";
import ConfirmationButtons from "../../views/table/ConfirmationButtons";
import { WithAlert, withAlert } from "../alert/withAlert";
import { Button } from "../button";
import Clickable from "../clickable";
import Dialog from "../dialog";
import { WithPromisedComponent, withPromisedComponent } from "../dialog/withPromisedComponent";
import { ArrowIcon, InfoIcon } from "../icon";
import Checkbox, { ICheckboxChange } from "../inputs/checkbox";
import Field from "../inputs/field";
import FieldsWrapper from "../inputs/field/FieldsWrapper";
import Input, { IInputOnBlurEvent, IInputOnChangeEvent } from "../inputs/input";
import { Separator } from "../separator/Separator";
import { IFieldDefFn, IGetValueArgs, TGetValueFn } from "../smart/FieldInfo";
import { phoneNumberInputFormatter, phoneNumberIsValid, phoneNumberParser } from "../smart/GeneralFieldDefinition";
import { SvgImage } from "../svgImage/SvgImage";
import Text from "../text";
import PurchaseFinishedDialog from "./PurchaseFinishedDialog";
import {
    ConsentCheckboxWrapper,
    DialogHeader,
    PaymentTypeImageWrapper,
    PlugAndPlayFormWrapper,
    ProductTypeSelectionScreenWrapper,
    PurchaseWizardTileFlex,
    SmallDescription,
    StyledPaymentTypeDescription,
    StyledSubItemDescription,
    StyledSubItemPrice,
    StyledSubItemTitle,
    StyledSubItemWrapper,
    SubItemPriceWrapper,
    SubItemTitleWrapper
} from "./PurchaseWizard.styles";
import {
    consentPath,
    fetchProductPrices,
    individualSettingsCompanyPath,
    individualSettingsTextPath,
    IPurchaseEvalaResponseData,
    PurchaseDialogPaymentType,
    purchaseEvala,
    PurchaseWizardDialogScreen
} from "./PurchaseWizardDialog.utils";
import PurchaseWizardDialogFormView from "./PurchaseWizardDialogFormView";
import PurchaseWizardTile from "./PurchaseWizardTile";

interface IProps extends WithTranslation, WithPromisedComponent, WithOData, WithAlert, RouteComponentProps {
    onClose: (finished: boolean) => void;
    onAfterTenantSave?: () => Promise<void>;
}

interface IState {
    isBusy: boolean;
    selectedProduct: ProductCode;
    screen: PurchaseWizardDialogScreen;
    paymentType: PurchaseDialogPaymentType;
    isConsentChecked: boolean;
    tenant: Partial<ITenantEntity>;
    phoneNumber: string;
    phoneNumberError: IValidationError;
    showWireFinished: boolean;
    showEvalaPurchased: boolean;
    productPrices: IBillingPricingEntity[];
    individualCompanyId: number;
}

class PurchaseWizardDialog extends React.PureComponent<IProps, IState> {
    static contextType = AppContext;

    formRef = React.createRef<FormViewForExtend<ITenantEntity>>();

    constructor(props: IProps, context: IAppContext) {
        super(props);

        this.state = {
            isBusy: true,
            selectedProduct: ProductCode.EvalaForAccountants,
            paymentType: PurchaseDialogPaymentType.Card,
            screen: PurchaseWizardDialogScreen.SelectProductType,
            isConsentChecked: true,
            tenant: null,
            phoneNumber: null,
            phoneNumberError: null,
            showWireFinished: false,
            showEvalaPurchased: false,
            productPrices: null,
            individualCompanyId: null
        };
    }

    componentDidMount() {
        this.init();
    }

    init = async (): Promise<void> => {
        const context = this.context as IAppContext;
        const reqTenant = this.props.oData.getEntitySetWrapper(EntitySetName.Tenants).query(context.getData().tenant.Id)
            .expand(TenantEntity.BillingAddress, q => q.expand(AddressEntity.Country))
            .fetchData<ITenantEntity>();
        const reqProductPrices = fetchProductPrices(this.props.oData);
        const [resTenant, resProductPrices] = await Promise.all([reqTenant, reqProductPrices]);

        const tenant = resTenant.value;
        const productPrices = resProductPrices.value;

        // for some reason, DateCreated is not readonly,
        // and currently our formatDateToDateTimeOffsetString sends wrong value on patch and backend fails
        // => delete DateCreated
        delete tenant.DateCreated;

        if (!tenant.PhoneNumber) {
            tenant.PhoneNumber = "+420";
        }

        this.setState({
            tenant,
            productPrices,
            isBusy: false
        });

        if (this.isEdu) {
            this.setState({
                screen: PurchaseWizardDialogScreen.InvoicingInfo
            });
        }
    };

    get isEdu(): boolean {
        return isEduLicenceWaitingForConfirmation(this.context.getData().subscription);
    }

    getTenantBc = memoizeOneWithParamsCheck(() => {
        const context = this.context as IAppContext;

        return createBindingContext(EntitySetName.Tenants, this.props.oData.getMetadata()).addKey(context.getData().tenant.Id);
    }, () => {
        const context = this.context as IAppContext;

        return [context.getData().tenant.Id];
    });

    formatAmount = (amount: number): string => {
        return Currency.format(amount, {
            currency: CurrencyCode.CzechKoruna,
            maximumFractionDigits: 0
        });
    };

    // i think this is used to prevent fetching data every time
    // the form screen is rendered (form of caching)
    syncFormEntityToState = (): void => {
        this.setState({
            tenant: this.formRef.current.entity,
            phoneNumber: this.formRef.current.entity.PhoneNumber
        });
    };

    handleProductSelection = (id: string): void => {
        this.setState({
            selectedProduct: id as ProductCode
        });
    };

    handlePaymentTypeSelection = (id: string): void => {
        const purchaseType = id as PurchaseDialogPaymentType;

        if (purchaseType === PurchaseDialogPaymentType.Card) {
            this.setState({
                phoneNumberError: null
            });
        }

        this.setState({
            paymentType: purchaseType
        });
    };

    handleConfirm = async (): Promise<void> => {
        if (this.state.screen === PurchaseWizardDialogScreen.InvoicingInfo) {
            this.syncFormEntityToState();

            this.setState({
                isBusy: true
            });

            const individualCompanyId = (this.formRef.current.entity as IEntity)[individualSettingsCompanyPath] as number;

            this.formRef.current.entity.BillingAddress.TenantId = this.formRef.current.entity.Id;

            const wasSaved = await this.formRef.current.save({
                updateEnabledFor: [TenantEntity.BillingAddress]
            });

            if (this.props.onAfterTenantSave) {
                try {
                    await this.props.onAfterTenantSave();
                } catch (e) {
                    const error = parseError(e);
                    const errorAlert = getAlertFromError(error);

                    this.setState({
                        isBusy: false
                    });
                    this.props.setAlert({
                        ...errorAlert
                        // detailData: data,
                    });

                    return;
                }
            }

            if (wasSaved && this.isEdu) {
                this.setState({
                    showEvalaPurchased: true
                });
            }

            this.setState({
                isBusy: false,
                individualCompanyId
            });

            if (!wasSaved) {
                return;
            }
        } else if (this.state.screen === PurchaseWizardDialogScreen.PaymentType) {
            const productCode = this.state.selectedProduct;
            const individualCompanyId = this.state.individualCompanyId;

            if (this.state.paymentType === PurchaseDialogPaymentType.Card) {
                this.setState({
                    isBusy: true
                });

                const res = await purchaseEvala({
                    paymentType: PurchaseDialogPaymentType.Card,
                    productCode: productCode,
                    phoneNumber: this.state.phoneNumber,
                    individualCompanyId: individualCompanyId
                });
                const data = await res.json();

                if (res.ok) {
                    window.location.href = (data as IPurchaseEvalaResponseData).Url;
                    return;
                } else {
                    const errorObject = parseError(data);
                    const errorAlert = getAlertFromError(errorObject);

                    this.props.setAlert({
                        ...errorAlert,
                        detailData: data
                    });

                    this.setState({
                        isBusy: false
                    });
                    return;
                }
            } else {
                const context = this.context as IAppContext;
                const isPhoneNumberValid = this.isPhoneNumberValid();

                if (!isPhoneNumberValid) {
                    this.setPhoneNumberError(true);
                    return;
                } else {
                    this.setState({
                        isBusy: true
                    });

                    const res = await purchaseEvala({
                        paymentType: PurchaseDialogPaymentType.Wire,
                        productCode: productCode,
                        phoneNumber: this.state.phoneNumber,
                        individualCompanyId: individualCompanyId
                    });

                    if (!res.ok) {
                        const error = await res.json();
                        this.props.setAlert({
                            status: Status.Error,
                            title: this.props.t("Common:Errors.ErrorHappened"),
                            detailData: error
                        });
                        this.setState({
                            isBusy: false
                        });
                        return;
                    } else {
                        const context = this.context as IAppContext;

                        // propagate the updated ProductCode into the context subscription
                        await context.updateTenantAndSubscription();

                        // some companies could've been deleted => update
                        await context.updateCompanies();

                        this.setState({
                            showWireFinished: true,
                            isBusy: false
                        });
                    }
                }
            }
        }

        this.setState({
            screen: this.state.screen + 1
        });
    };

    handleCancel = (): void => {
        this.props.onClose(false);
    };

    handleBack = (): void => {
        this.props.setAlert(null);

        if (this.state.screen === PurchaseWizardDialogScreen.InvoicingInfo) {
            this.syncFormEntityToState();
        }

        this.setState({
            screen: this.state.screen - 1
        });
    };

    getBillingInfoDefinition = (): IDefinition => {
        const tenantFormDef = getDefinition(this.context);
        const addressGroup = tenantFormDef.form.groups.find(group => group.id === "address");
        const rows = addressGroup.rows;
        const groups = [{
            ...addressGroup,
            rows
        }];

        const hasCompaniesFn: TGetValueFn<boolean> = (args: IGetValueArgs) => {
            return args.storage.context.getData()?.companies?.length > 1;
        };

        if (this.state.selectedProduct !== ProductCode.EvalaForCompanies) {
            rows.push([{ id: consentPath }]);
        } else {
            groups.push({
                id: "individualSettings",
                title: i18next.t("Subscriptions:ProductSelection.IndividualSettingsTitle"),
                isVisible: hasCompaniesFn,
                rows: [
                    [{ id: individualSettingsTextPath }],
                    [{ id: individualSettingsCompanyPath }]
                ]
            });
        }

        tenantFormDef.form.fieldDefinition[TenantEntity.LegalNumber] = {
            ...tenantFormDef.form.fieldDefinition[TenantEntity.LegalNumber],
            isRequired: true
        };

        if (this.isEdu) {
            tenantFormDef.form.fieldDefinition[createPath(TenantEntity.BillingAddress, BillingAddressEntity.Name)] = {
                ...tenantFormDef.form.fieldDefinition[createPath(TenantEntity.BillingAddress, BillingAddressEntity.Name)],
                label: this.props.t(`Subscriptions:ProductSelection.EduInstitutionName`),
                width: BasicInputSizes.XL
            };
        }

        return {
            ...tenantFormDef,
            form: {
                ...tenantFormDef.form,
                fieldDefinition: {
                    ...tenantFormDef.form.fieldDefinition,
                    [consentPath]: {
                        type: FieldType.Custom,
                        labelStatus: LabelStatus.Removed,
                        render: (args: IFieldDefFn) => {
                            const handleChange = (event: ICheckboxChange) => {
                                this.setState({
                                    isConsentChecked: event.value
                                });
                                args.storage.refresh();
                            };

                            return (
                                <ConsentCheckboxWrapper>
                                    <Checkbox checked={this.state.isConsentChecked}
                                              onChange={handleChange}
                                              label={<>
                                                  {`${this.props.t(`Subscriptions:ProductSelection.${this.isEdu ? "ConsentPrefixEdu" : "ConsentPrefix"}`)} `}
                                                  <Clickable
                                                      link={this.isEdu ? EvalaTermsAndConditionsEduLink : EvalaTermsAndConditionsLink}>
                                                      {this.props.t(`Subscriptions:ProductSelection.${this.isEdu ? "ConsentVOPEdu" : "ConsentVOP"}`)}
                                                  </Clickable>
                                              </>}/>
                                </ConsentCheckboxWrapper>
                            );
                        }
                    },
                    [individualSettingsTextPath]: {
                        type: FieldType.Custom,
                        render: () => {
                            return (
                                <Text>
                                    {this.props.t("Subscriptions:ProductSelection.IndividualSettingsDescription")}
                                </Text>
                            );
                        }

                    },
                    [individualSettingsCompanyPath]: {
                        label: this.props.t("Subscriptions:ProductSelection.IndividualSettingsCompany"),
                        type: FieldType.ComboBox,
                        isVisible: hasCompaniesFn,
                        isRequired: true,
                        width: BasicInputSizes.L
                    }
                },
                groups
            }
        };
    };

    getProductTypePrice = (productTypeCode: BillingModuleTypeCode): number => {
        const productPrice = this.state.productPrices.find(p => p.BillingModuleTypeCode === productTypeCode);

        if (!productPrice) {
            return null;
        }

        return productPrice.Price;
    };

    renderProductTypeSelectionScreen = (): React.ReactElement => {
        if (!this.state.productPrices) {
            return null;
        }

        return (
            <ProductTypeSelectionScreenWrapper>
                <PurchaseWizardTileFlex>
                    <PurchaseWizardTile id={ProductCode.EvalaForAccountants}
                                        title={this.props.t("Subscriptions:ProductSelection.ForAccountants")}
                                        isSelected={this.state.selectedProduct === ProductCode.EvalaForAccountants}
                                        onClick={this.handleProductSelection}>
                        <SubItemWrapper>
                            <SubItemTitle>
                                {this.props.t("Subscriptions:ProductSelection.AccountingForBusiness")}
                            </SubItemTitle>
                            <SubItemPriceWrapper>
                                <SubItemPrice>
                                    {this.formatAmount(this.getProductTypePrice(BillingModuleTypeCode.DoubleEntry))}
                                </SubItemPrice>
                                <SubItemDescription>
                                    {this.props.t("Subscriptions:ProductSelection.Customer")} / {this.props.t("Subscriptions:ProductSelection.Month")}
                                </SubItemDescription>
                            </SubItemPriceWrapper>
                        </SubItemWrapper>

                        <Separator isEmphasised/>

                        <SubItemWrapper>
                            <SubItemTitle>
                                {this.props.t("Subscriptions:ProductSelection.CashBasisAccounting")}
                            </SubItemTitle>
                            <SubItemPriceWrapper>
                                <SubItemPrice>
                                    {this.formatAmount(this.getProductTypePrice(BillingModuleTypeCode.CashBased))}
                                </SubItemPrice>
                                <SubItemDescription>
                                    {this.props.t("Subscriptions:ProductSelection.Customer")} / {this.props.t("Subscriptions:ProductSelection.Month")}
                                </SubItemDescription>
                            </SubItemPriceWrapper>
                        </SubItemWrapper>

                        <Separator isEmphasised/>

                        <SubItemWrapper>
                            <SubItemTitle>
                                {this.props.t("Subscriptions:ProductSelection.PayrollEvidence")}
                            </SubItemTitle>
                            <SubItemPriceWrapper>
                                <SubItemPrice>
                                    {this.formatAmount(this.getProductTypePrice(BillingModuleTypeCode.Payroll))}
                                </SubItemPrice>
                                <SubItemDescription>
                                    {this.props.t("Subscriptions:ProductSelection.Customer")} / {this.props.t("Subscriptions:ProductSelection.Month")}
                                </SubItemDescription>
                            </SubItemPriceWrapper>
                        </SubItemWrapper>

                        {/*<Separator isEmphasised/>

                        <SubItemWrapper>
                            <SubItemTitle>
                                {this.props.t("Subscriptions:ProductSelection.ArchivedAgenda")}
                            </SubItemTitle>
                            <SubItemPriceWrapper>
                                <SubItemPrice>
                                    {this.formatAmount(this.getProductTypePrice(BillingModuleTypeCode.Archived))}
                                </SubItemPrice>
                                <SubItemDescription>
                                    {this.props.t("Subscriptions:ProductSelection.Customer")} / {this.props.t("Subscriptions:ProductSelection.Month")}
                                </SubItemDescription>
                            </SubItemPriceWrapper>
                        </SubItemWrapper>*/}
                    </PurchaseWizardTile>
                    <PurchaseWizardTile id={ProductCode.EvalaForCompanies}
                                        title={this.props.t("Subscriptions:ProductSelection.ForCompanies")}
                                        description={this.props.t("Subscriptions:ProductSelection.ForCompaniesDescription")}
                                        isSelected={this.state.selectedProduct === ProductCode.EvalaForCompanies}
                                        onClick={this.handleProductSelection}>
                        <SubItemWrapper>
                            <SubItemTitleWrapper>
                                <SubItemTitle>
                                    {this.props.t("Subscriptions:ProductSelection.AccountingForBusiness")}
                                </SubItemTitle>
                            </SubItemTitleWrapper>
                            <SubItemPriceWrapper>
                                <SubItemPrice>
                                    {this.formatAmount(this.getProductTypePrice(BillingModuleTypeCode.IndividualDoubleEntry))}
                                </SubItemPrice>
                                <SubItemDescription>
                                    {this.props.t("Subscriptions:ProductSelection.User")} / {this.props.t("Subscriptions:ProductSelection.Month")}
                                </SubItemDescription>
                            </SubItemPriceWrapper>
                        </SubItemWrapper>

                        <Separator isEmphasised/>

                        <SubItemWrapper>
                            <SubItemTitleWrapper>
                                <SubItemTitle>
                                    {this.props.t("Subscriptions:ProductSelection.CashBasisAccounting")}
                                </SubItemTitle>
                            </SubItemTitleWrapper>
                            <SubItemPriceWrapper>
                                <SubItemPrice>
                                    {this.formatAmount(this.getProductTypePrice(BillingModuleTypeCode.IndividualCashBased))}
                                </SubItemPrice>
                                <SubItemDescription>
                                    {this.props.t("Subscriptions:ProductSelection.User")} / {this.props.t("Subscriptions:ProductSelection.Month")}
                                </SubItemDescription>
                            </SubItemPriceWrapper>
                        </SubItemWrapper>
                    </PurchaseWizardTile>
                </PurchaseWizardTileFlex>
                <SmallDescription data-testid={TestIds.Description}>
                    <InfoIcon width={IconSize.XS}/>
                    <span>
                        <Trans i18nKey={"Subscriptions:ProductSelection.AboveDocumentsLimit"}
                               values={{
                                   extraDocument: this.formatAmount(this.getProductTypePrice(BillingModuleTypeCode.ExtraDocument)),
                                   extraPayroll: this.formatAmount(this.getProductTypePrice(BillingModuleTypeCode.PayrollOverLimit))
                               }}
                               components={{
                                   1: <strong/>,
                                   2: <strong/>,
                                   // eslint-disable-next-line jsx-a11y/anchor-has-content
                                   3: <a href="https://www.evala.cz/cenik" target="_blank" rel="noopener noreferrer"/>
                               }}/>
                    </span>
                </SmallDescription>
            </ProductTypeSelectionScreenWrapper>
        );
    };

    renderBillingInfoScreen = () => {
        return (
            // hack to prevent form from having scrollbar (by removing height 100%)
            <PlugAndPlayFormWrapper>
                <PlugAndPlayForm
                    getDef={this.getBillingInfoDefinition}
                    formRef={this.formRef}
                    t={this.props.t}
                    bindingContext={this.getTenantBc()}
                    initialData={{
                        entity: this.state.tenant
                    }}
                    ignoreVariants
                    formView={PurchaseWizardDialogFormView}
                    formViewProps={{
                        isInDialog: true,
                        formProps: {
                            hideHeader: true,
                            hideBreadcrumbs: true,
                            renderScrollbar: false,
                            withoutPadding: true
                        },
                        hideButtons: true
                    }}
                />
            </PlugAndPlayFormWrapper>
        );
    };

    isPhoneNumberValid = (): boolean => {
        return phoneNumberIsValid(this.state.phoneNumber);
    };

    setPhoneNumberError = (isError: boolean) => {
        this.setState({
            phoneNumberError: isError ? {
                message: this.props.t("Common:Validation.WrongNumber")
            } : null
        });
    };

    handlePhoneNumberChange = (e: IInputOnChangeEvent<string>): void => {
        this.setState({
            phoneNumber: e.value
        });
    };

    handlePhoneNumberBlur = (e: IInputOnBlurEvent): void => {
        this.setPhoneNumberError(!this.isPhoneNumberValid());
    };

    handleFinishedDialogClose = (): void => {
        if (this.props.history.location.pathname !== ROUTE_SUBSCRIPTIONS_PLAN) {
            // Wire mode selected - current company could've been removed
            // => redirect to ROUTE_SUBSCRIPTIONS_PLAN just to show safe page
            const context = this.context as IAppContext;
            // change to our agenda, just to be sure
            context.setCurrentCompanyId(null);

            // redirect to /org/plan if not already there
            this.props.history.push(ROUTE_SUBSCRIPTIONS_PLAN);
        }
        this.props.onClose(true);
    };

    renderPaymentTypeScreen = (): React.ReactElement => {
        return (
            <PurchaseWizardTileFlex>
                <PurchaseWizardTile id={PurchaseDialogPaymentType.Card}
                                    title={this.props.t("Subscriptions:ProductSelection.PaymentCard")}
                                    isCentered
                                    isSelected={this.state.paymentType === PurchaseDialogPaymentType.Card}
                                    isWithoutDescription
                                    onClick={this.handlePaymentTypeSelection}>
                    <PaymentTypeImageWrapper>
                        <SvgImage src={billingPayCardSvg}/>
                    </PaymentTypeImageWrapper>
                    <PaymentTypeDescription>
                        {this.props.t("Subscriptions:ProductSelection.PaymentCardDescription")}
                    </PaymentTypeDescription>
                </PurchaseWizardTile>
                <PurchaseWizardTile id={PurchaseDialogPaymentType.Wire}
                                    title={this.props.t("Subscriptions:ProductSelection.PaymentWire")}
                                    isCentered
                                    isSelected={this.state.paymentType === PurchaseDialogPaymentType.Wire}
                                    isWithoutDescription
                                    onClick={this.handlePaymentTypeSelection}>
                    <PaymentTypeImageWrapper>
                        <SvgImage src={billingPayWireSvg}/>
                    </PaymentTypeImageWrapper>
                    <PaymentTypeDescription>
                        {this.props.t("Subscriptions:ProductSelection.PaymentWireDescription")}
                    </PaymentTypeDescription>
                    <FieldsWrapper>
                        <Field id={"phoneNumber"}
                               label={this.props.t("Subscriptions:ProductSelection.PhoneNumber")}
                               width={BasicInputSizes.L}
                               isRequired>
                            <Input id={"phoneNumber"}
                                   width={BasicInputSizes.L}
                                   onChange={this.handlePhoneNumberChange}
                                   onBlur={this.handlePhoneNumberBlur}
                                   value={this.state.phoneNumber}
                                   error={this.state.phoneNumberError}
                                   formatter={phoneNumberInputFormatter}
                                   parser={phoneNumberParser}
                                   isValid={phoneNumberIsValid}
                            />
                        </Field>
                    </FieldsWrapper>
                </PurchaseWizardTile>
            </PurchaseWizardTileFlex>
        );
    };

    renderHeader = () => {
        if (this.isEdu) {
            return (
                <DialogHeader data-testid={TestIds.DialogHeader}>
                    {this.props.t(`Subscriptions:ProductSelection.HeaderEdu`)}
                </DialogHeader>
            );
        }

        let titleKey: string;

        switch (this.state.screen) {
            case PurchaseWizardDialogScreen.SelectProductType:
                titleKey = "HeaderSelectPlanType";
                break;

            case PurchaseWizardDialogScreen.InvoicingInfo:
                titleKey = "HeaderInvoicingInfo";
                break;
            case PurchaseWizardDialogScreen.PaymentType:
                titleKey = "HeaderPaymentType";
                break;
        }

        const title = this.props.t(`Subscriptions:ProductSelection.${titleKey}`);

        return (
            <DialogHeader data-testid={TestIds.DialogHeader}>
                {`${this.state.screen + 1}/${Object.keys(PurchaseWizardDialogScreen).filter(key => isNaN(Number(key))).length}: ${title}`}
            </DialogHeader>
        );
    };

    renderFooter = () => {
        if (this.isEdu) {
            return (
                <ConfirmationButtons onConfirm={this.handleConfirm}
                                     onCancel={this.handleCancel}
                                     isDisabled={!this.state.isConsentChecked}
                                     confirmText={this.props.t("Common:General.Save")}>
                </ConfirmationButtons>
            );
        }

        let isContinueDisabled = false;

        if (this.state.screen === PurchaseWizardDialogScreen.InvoicingInfo) {
            isContinueDisabled = !this.state.isConsentChecked && this.state.selectedProduct !== ProductCode.EvalaForCompanies;
        }

        return (
            <ConfirmationButtons onConfirm={this.handleConfirm}
                                 onCancel={this.handleCancel}
                                 isDisabled={isContinueDisabled}
                                 confirmText={this.state.screen < 2
                                     ? this.props.t("Common:General.Continue")
                                     : this.props.t("Subscriptions:ProductSelection.CompletePurchase")}>
                {this.state.screen > 0 ?
                    <Button onClick={this.handleBack}
                            icon={<ArrowIcon width={IconSize.S} height={IconSize.S}/>}
                            isTransparent>
                        {this.props.t("Common:General.Back")}
                    </Button>
                    : null}
            </ConfirmationButtons>
        );
    };

    renderScreen = (): React.ReactElement => {
        switch (this.state.screen) {
            case PurchaseWizardDialogScreen.SelectProductType:
                return this.renderProductTypeSelectionScreen();
            case PurchaseWizardDialogScreen.InvoicingInfo:
                return this.renderBillingInfoScreen();
            case PurchaseWizardDialogScreen.PaymentType:
                return this.renderPaymentTypeScreen();
        }
    };

    render() {
        if (!this.props.tReady) {
            return null;
        }

        if (this.state.showWireFinished || this.state.showEvalaPurchased) {
            return <PurchaseFinishedDialog
                type={this.state.showWireFinished ? PurchaseDialogPaymentType.Wire : PurchaseDialogPaymentType.Card}
                onClose={this.handleFinishedDialogClose}/>;
        }

        return (
            <Dialog title={this.props.t("Subscriptions:ProductSelection.DialogTitle")}
                    onConfirm={this.handleConfirm}
                    onClose={this.handleCancel}
                    footer={this.renderFooter()}
                    busy={this.state.isBusy}
                    width={"1000px"}
                    height={"710px"}>
                {this.renderHeader()}
                {this.props.alert}
                {this.renderScreen()}
            </Dialog>
        );
    }
}

export const SubItemWrapper: React.FunctionComponent = (props): React.ReactElement => {
    return (
        <StyledSubItemWrapper data-testid={TestIds.SubItem}>
            {props.children}
        </StyledSubItemWrapper>
    );
};

export const SubItemTitle: React.FunctionComponent = (props): React.ReactElement => {
    return (
        <StyledSubItemTitle data-testid={TestIds.SubItemTitle}>
            {props.children}
        </StyledSubItemTitle>
    );
};

export const SubItemPrice: React.FunctionComponent = (props): React.ReactElement => {
    return (
        <StyledSubItemPrice data-testid={TestIds.Price}>
            {props.children}
        </StyledSubItemPrice>
    );
};

export const SubItemDescription: React.FunctionComponent = (props): React.ReactElement => {
    return (
        <StyledSubItemDescription data-testid={TestIds.SubItemDescription}>
            {props.children}
        </StyledSubItemDescription>
    );
};

export const PaymentTypeDescription: React.FunctionComponent<{
    style?: React.CSSProperties;
}> = (props): React.ReactElement => {
    return (
        <StyledPaymentTypeDescription data-testid={TestIds.PaymentTypeDescription}
                                      style={props.style}>
            {props.children}
        </StyledPaymentTypeDescription>
    );
};

export default withRouter(withTranslation(["Subscriptions", "Common"])(withPromisedComponent(withOData(withAlert({
    isFullWidth: true,
    isOneLiner: true
})(PurchaseWizardDialog)))));